import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import LoadingIndicator from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Text16 } from '@sorare/core/src/atoms/typography';
import Dialog from '@sorare/core/src/components/dialog';
import { isType } from '@sorare/core/src/gql';
import useQuery from '@sorare/core/src/hooks/graphql/useQuery';

import { LastGamesPerformanceDialogBody } from '../LastGamesPerformanceDialogBody';
import {
  NBALastGamesPerformanceDialogQuery,
  NBALastGamesPerformanceDialogQueryVariables,
} from './__generated__/index.graphql';

const Body = styled.div`
  padding: var(--triple-unit);
`;

type Props = {
  open: boolean;
  onClose: () => void;
} & ContentProps;

type ContentProps = {
  gameId: string;
  slug: string;
};

export const LAST_GAMES_PERFORMANCE_DIALOG_QUERY = gql`
  query NBALastGamesPerformanceDialogQuery($slug: String!) {
    anyPlayer(slug: $slug) {
      slug
      ... on NBAPlayer {
        slug
        ...NBALastGamesPerformanceDialogBody_player
        # eslint-disable-next-line @graphql-eslint/no-deprecated
        latestFixtureStats(last: 20) {
          fixture {
            slug
            fixtureState
          }
          status {
            gameStats {
              ...NBALastGamesPerformanceDialogBody_gameStats
            }
          }
        }
      }
    }
  }
  ${LastGamesPerformanceDialogBody.fragments.gameStats}
  ${LastGamesPerformanceDialogBody.fragments.player}
` as TypedDocumentNode<
  NBALastGamesPerformanceDialogQuery,
  NBALastGamesPerformanceDialogQueryVariables
>;

const LastGamesPerformanceDialogContents = (props: ContentProps) => {
  const { slug, gameId } = props;

  const { data } = useQuery(LAST_GAMES_PERFORMANCE_DIALOG_QUERY, {
    variables: { slug },
  });

  if (!data) {
    return <LoadingIndicator />;
  }
  const { anyPlayer } = data;
  if (!isType(anyPlayer, 'NBAPlayer')) {
    return null;
  }

  const fixturesGames = anyPlayer.latestFixtureStats.flatMap(
    fixtureStat => fixtureStat.status.gameStats
  );

  const gameStats = fixturesGames.find(stats => stats.game.id === gameId);

  if (!gameStats) {
    return (
      <FormattedMessage
        id="Nba.LastGamesPerformanceDialog.noStats"
        defaultMessage="Stats not available for this game."
      />
    );
  }

  return (
    <LastGamesPerformanceDialogBody player={anyPlayer} gameStats={gameStats} />
  );
};

const LastGamesPerformanceDialog = ({
  onClose,
  open,
  ...contentProps
}: Props) => {
  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      title={
        <Text16>
          <FormattedMessage
            id="LastGamesPerformanceDialog.title"
            defaultMessage="Player performance"
          />
        </Text16>
      }
      onClose={onClose}
    >
      <Body>
        <LastGamesPerformanceDialogContents {...contentProps} />
      </Body>
    </Dialog>
  );
};

export default LastGamesPerformanceDialog;

LastGamesPerformanceDialog.fragments = LastGamesPerformanceDialogBody.fragments;
